import { Filter, Model, ObservableList, PropertyValueInvalidations, ViewModel } from '@neos/core'
import { getPropertyByPath } from '@neos/shared'
import { CustomViewStyleRuleContentDisplayMode } from '@src/enums/CustomViewStyleRuleContentDisplayMode'
import { CustomViewContentAlign } from '@src/enums/CustomViewContentAlign'

/**
 * Represents CustomSubUIViewStyleRuleUI.
 */
export default class CustomSubUIViewStyleRuleUI extends Model {
  /**
   * Initializes a new instance of the CustomSubUIViewStyleRuleUI class.
   * 
   * @param item Item.
   * @param viewModel ViewModel.
   */
  constructor(item?: Record<string, unknown>, viewModel?: ViewModel<CustomSubUIViewStyleRuleUI>) {
    super({
      $new: !item,
      enabled: true,
      executionOrder: 0,
      id: 0,
      ...item
    }, viewModel)
  }

  /**
   * Gets or sets Application condition.
   */
  applicationCondition?: string | null

  /**
   * Gets or sets ApplicationConditionObject.
   */
  applicationConditionObject: Filter

  /**
   * Gets or sets Bold text.
   */
  bold?: boolean | null

  /**
   * Gets or sets Content display mode.
   */
  contentDisplayMode?: CustomViewStyleRuleContentDisplayMode | null

  /**
   * Gets or sets Background color (dark theme).
   */
  darkBackgroundColor?: string | null

  /**
   * Gets or sets Foreground color (dark theme).
   */
  darkForegroundColor?: string | null

  /**
   * Gets editedApplicationConditionObject.
   * 
   * @returns EditedApplicationConditionObject.
   */
  get editedApplicationConditionObject(): Filter {
    return this.$viewModel.getProperty('editedApplicationConditionObject').getValue(this) as Filter
  }

  /**
   * Sets editedApplicationConditionObject.
   * 
   * @param value EditedApplicationConditionObject.
   */
  set editedApplicationConditionObject(value: Filter) {
    this.$viewModel.getProperty('editedApplicationConditionObject').setValue(this, value)
  }

  /**
   * Gets or sets Enabled.
   */
  enabled: boolean

  /**
   * Gets or sets Execution order.
   */
  executionOrder?: number | null

  /**
   * Gets or sets Horizontal alignment.
   */
  horizontalAlign?: CustomViewContentAlign | null

  /**
   * Gets or sets Icon.
   */
  iconName?: string | null

  /**
   * Gets or sets Identifier.
   */
  id: number

  /**
   * Gets or sets Italic text.
   */
  italic?: boolean | null

  /**
   * Gets or sets Background color (light theme).
   */
  lightBackgroundColor?: string | null

  /**
   * Gets or sets Foreground color (light theme).
   */
  lightForegroundColor?: string | null

  /**
   * Gets propertyCaption.
   * 
   * @returns PropertyCaption.
   */
  get propertyCaption(): (string | null | undefined) {
    return this.$viewModel.getProperty('propertyCaption').getValue(this) as (string | null | undefined)
  }

  /**
   * Gets or sets Target column.
   */
  propertyName?: string | null

  /**
   * Gets or sets Strikethrough text.
   */
  strikethrough?: boolean | null

  /**
   * Gets or sets Underline text.
   */
  underline?: boolean | null

  /**
   * Gets or sets Custom sub view identifier.
   */
  customSubUIViewId?: number

  /**
   * Gets or sets $keyProperties.
   */
  static $keyProperties = new Set(['id'])

  /**
   * Gets or sets $unboundProperties.
   */
  static $unboundProperties = new Set(['applicationConditionObject','editedApplicationConditionObject','propertyCaption'])

  /**
   * Gets or sets $untrackedProperties.
   */
  static $untrackedProperties = new Set(['applicationConditionObject','editedApplicationConditionObject','propertyCaption'])

  /**
   * Gets $propertyValueInvalidations.
   */
  static readonly $propertyValueInvalidations: PropertyValueInvalidations = {
    async iconName(item: Model, invalidateLookupValue: boolean) {
      if (!item.$suspendedPropertyValueInvalidations.has('iconName')) {
        await Promise.all([
          (() => {
            const propertyValue = getPropertyByPath(item, 'iconName')
            const getValueToDisplayInLookupPromise = item.getLookupItemPropertyValueAsync('iconName', 'description', 'value', propertyValue)
            return [
              item.setAdditionalDataAsync('IconName_DisplayProperty', getValueToDisplayInLookupPromise.then(v => v.isValid ? Promise.resolve(v.value) : Promise.reject())),
              invalidateLookupValue && item.invalidateLookupValueAsync('iconName', propertyValue, getValueToDisplayInLookupPromise)
            ]
          })()
        ])
      }
    }
  }

  /**
   * GetIconNameDisplayValueAsync.
   */
  async getIconNameDisplayValueAsync(): Promise<string | null> {
    const displayValue = await this.getLookupDisplayValueAsync<string | null>('IconName')
    return displayValue
  }

  /**
   * TrySetIconNameAsync.
   * 
   * @param value Value.
   * @param displayValue DisplayValue.
   */
  async trySetIconNameAsync(value: string | null, displayValue: string | null = null): Promise<boolean> {
    const result = await this.trySetLookupValueAsync('IconName', value, displayValue)
    return result
  }

  /**
   * TrySetIconNameFromDisplayValueAsync.
   * 
   * @param displayValue DisplayValue.
   */
  async trySetIconNameFromDisplayValueAsync(displayValue: string | null): Promise<boolean> {
    const result = await this.trySetLookupValueFromDisplayValueAsync('IconName', displayValue)
    return result
  }
}